import React, {
  forwardRef,
  useImperativeHandle,
  useRef,
  useState,
  useEffect,
  useCallback,
} from 'react';
import { makePrioStyles } from '../../../../theme/utils';
import { Button } from '@prio365/prio365-react-library';
import TextBlocksModal from '../../../settings/components/TextBlocksModal';
import { TextBlock } from '../../../../models/TextBlock';
import { useTranslation } from 'react-i18next';
import {
  faSignature,
  faMagicWandSparkles,
} from '@fortawesome/pro-regular-svg-icons';

import { v4 as uuid } from 'uuid';
import { icon } from '@fortawesome/fontawesome-svg-core';
import SignatureInsertModal from './SignatureInsert/SignatureModal';
import FroalaEditorInstance from 'froala-editor';
import FroalaEditor, {
  FroalaConfig,
} from '../../../../components/Froala/FroalaEditor';
import {
  DEFAULT_BR_VALUE,
  DEFAULT_FROALA_CONFIG,
  DEFAULT_FROALA_EVENTS,
  DEFAULT_TOOLBAR_BUTTONS,
} from '../../../../components/Froala/config';
import { useSelector } from 'react-redux';
import {
  getIsSettingEnabled,
  getSettingsByKey,
  RootReducerState,
} from '../../../../apps/main/rootReducer';
import { PrioTheme } from '../../../../theme/types';

import './AiAssistantPlugin';
import '../../../../components/Froala/plugins/tableCellBorderPlugin';
import { Setting } from '../../../../models/Settings';

// Performance-Optimierung: Verhindere mehrfache Icon/Command-Registrierung
let iconsAndCommandsRegistered = false;

const registerIconsAndCommands = (
  t: any,
  isAIFeaturesEnabled: boolean,
  callbacks: any
) => {
  if (iconsAndCommandsRegistered) return;

  try {
    // Icons definieren
    FroalaEditorInstance.DefineIconTemplate('font_awesome', '[HTML]');
    FroalaEditorInstance.DefineIconTemplate('font_awesome_quick', '[HTML]');

    FroalaEditorInstance.DefineIcon('textBlocksIcon', {
      NAME: 'file-alt',
      template: 'font_awesome_quick',
    });

    FroalaEditorInstance.DefineIcon('textBlocksQuickIcon', {
      NAME: 'file-alt',
      SVG_KEY: 'insertMore',
    });

    FroalaEditorInstance.DefineIcon('signatureInsertIcon', {
      NAME: 'signature',
      template: 'font_awesome_quick',
      HTML: [
        icon(faSignature).html[0].slice(0, 4),
        " style='margin-bottom: 4px'",
        icon(faSignature).html[0].slice(4),
      ].join(''),
    });

    if (isAIFeaturesEnabled) {
      FroalaEditorInstance.DefineIcon('aiAssistantIcon', {
        NAME: 'magic-wand-sparkles',
        template: 'font_awesome_quick',
        HTML: [
          icon(faMagicWandSparkles).html[0].slice(0, 4),
          " style='margin-bottom: 4px'",
          icon(faMagicWandSparkles).html[0].slice(4),
        ].join(''),
      });
    }

    FroalaEditorInstance.DefineIcon('clearCustomFormattingIcon', {
      NAME: 'clearCustomFormattingIcon',
      SVG_KEY: 'clearFormatting',
    });

    FroalaEditorInstance.DefineIcon('pastePlainTextIcon', {
      NAME: 'pastePlainTextIcon',
      SVG_KEY: 'clearFormatting',
    });

    // ===== COMMANDS REGISTRIEREN =====
    FroalaEditorInstance.RegisterCommand('textBlocks', {
      title: t('mail:composer.textBlocks'),
      undo: true,
      refreshAfterCallback: true,
      callback: function () {
        // Ensure callbacks exist and setShowTextBlocksModal is a function
        if (
          callbacks &&
          typeof callbacks.setShowTextBlocksModal === 'function'
        ) {
          callbacks.setShowTextBlocksModal(true);
        } else {
          console.error('setShowTextBlocksModal callback not available');
        }
      },
      icon: 'textBlocksQuickIcon',
    });

    FroalaEditorInstance.RegisterCommand('clearCustomFormatting', {
      title: t('mail:composer.clearFormatting'),
      focus: false,
      undo: true,
      refreshAfterCallback: true,
      callback: function () {
        if (
          callbacks &&
          typeof callbacks.handleClearFormatting === 'function'
        ) {
          callbacks.handleClearFormatting();
        } else {
          console.error('handleClearFormatting callback not available');
        }
      },
      icon: 'clearCustomFormattingIcon',
    });

    if (isAIFeaturesEnabled) {
      FroalaEditorInstance.RegisterCommand('aiAssistant', {
        title: t('mail:composer.aiAssistant', 'AI Assistant') + ' (Ctrl+K)',
        focus: false,
        undo: false,
        refreshAfterCallback: false,
        callback: function () {
          if (
            this.aiAssistant &&
            typeof this.aiAssistant.openAiAssistant === 'function'
          ) {
            this.aiAssistant.openAiAssistant();
          }
        },
        icon: 'aiAssistantIcon',
      });
    }

    // ===== QUICK INSERT BUTTONS =====
    FroalaEditorInstance.RegisterQuickInsertButton('signatureInsert', {
      title: t('mail:composer.signature'),
      undo: true,
      refreshAfterCallback: true,
      callback: function () {
        if (
          callbacks &&
          typeof callbacks.setShowInsertSignatureModal === 'function'
        ) {
          callbacks.setShowInsertSignatureModal(true);
        } else {
          console.error('setShowInsertSignatureModal callback not available');
        }
      },
      icon: 'signatureInsertIcon',
    });

    FroalaEditorInstance.RegisterQuickInsertButton('textBlocksQuick', {
      title: t('mail:composer.textBlocks'),
      undo: true,
      refreshAfterCallback: true,
      callback: function () {
        if (
          callbacks &&
          typeof callbacks.setShowTextBlocksModal === 'function'
        ) {
          callbacks.setShowTextBlocksModal(true);
        } else {
          console.error('setShowTextBlocksModal callback not available');
        }
      },
      icon: 'textBlocksQuickIcon',
    });

    FroalaEditorInstance.RegisterQuickInsertButton('pastePlainText', {
      title: t('mail:composer.pastePlain'),
      undo: true,
      refreshAfterCallback: true,
      callback: function () {
        if (
          callbacks &&
          typeof callbacks.handleInsertPlainFromClipboard === 'function'
        ) {
          callbacks.handleInsertPlainFromClipboard();
        } else {
          console.error(
            'handleInsertPlainFromClipboard callback not available'
          );
        }
      },
      icon: 'pastePlainTextIcon',
    });

    iconsAndCommandsRegistered = true;
  } catch (error) {
    console.error('Error registering icons and commands:', error);
    iconsAndCommandsRegistered = false;
  }
};

const useStyles = makePrioStyles((theme: PrioTheme) => ({
  root: {
    position: 'relative',
    flex: 1,
    overflow: 'hidden',
    '& .fr-box.fr-basic .fr-wrapper': {
      border: 'none',
    },
    '& .fr-box.fr-basic': {
      borderRadius: 0,
      height: '100%',
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column',
    },
    '& .fr-box.fr-basic.fr-bottom .fr-wrapper': {
      border: 'none',
      borderRadius: 0,
      flex: 1,
      overflow: 'scroll',
    },
    '& .fr-box.fr-basic .fr-element': {
      padding: theme.old.spacing.unit(2),
      fontFamily: 'inherit',
    },
    '& .fr-toolbar.fr-bottom': {
      border: 'none',
      borderRadius: 0,
      '& .fr-newline': {
        display: 'none',
      },
    },
    '& .fr-toolbar .fr-btn-grp': {
      margin: `0 ${theme.old.spacing.unit(2)}px`,
    },
    '& .fr-toolbar .fr-command.fr-btn.fr-active, .fr-popup .fr-command.fr-btn.fr-active, .fr-modal .fr-command.fr-btn.fr-active':
      {
        color: 'var(--ant-primary-5)',
      },
    '& .fr-toolbar .fr-more-toolbar': {
      position: 'inherit',
    },
    '& .fr-quick-insert': {
      left: '64px!important',
    },
    '& .fr-toolbar .fr-command.fr-btn, .fr-popup .fr-command.fr-btn, .fr-modal .fr-command.fr-btn':
      {
        borderRadius: 2,
      },
    '& .fr-toolbar.fr-bottom .fr-command.fr-btn.fr-open': {
      borderRadius: '0 0 2px 2px',
    },
  },
  aiAssistantButton: {
    position: 'absolute',
    top: theme.old?.spacing?.unit?.(1) || 8,
    right: theme.old?.spacing?.unit?.(1) || 8,
    zIndex: 100,
    borderRadius: '50%',
    width: '32px',
    height: '32px',
    minWidth: '32px',
    transition: 'all 0.2s ease',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    '&:hover': {
      transform: 'scale(1.05)',
    },
    '&:active': {
      transform: 'scale(0.95)',
    },
    '& .prio-button-content': {
      fontSize: '14px',
      fontWeight: 700,
    },
  },
  editorContainer: {
    position: 'relative',
  },
}));

const createFileInEditor = (file: Blob, editor: FroalaEditorInstance) => {
  let imageUUID = uuid();

  let url = URL.createObjectURL(file);
  editor.image.insert(
    url,
    null,
    { name: 'inline-image-' + imageUUID, id: imageUUID },
    editor.image.get()
  );
};

export interface MailEditorRefProps {
  getHtml: () => string;
  openAiAssistant?: () => void;
}

interface MailEditorProps {
  toolbarContainer: string;
  body?: string;
  textUpdated?: (body: string) => void;
  disabled?: boolean;
  autofocus?: boolean;
  messageIdentifier?: string;
  projectId?: string;
  isDraftIncluded?: boolean;
  messageSubject?: string;
  showAiAssistantButton?: boolean;
}

export const MailEditor = forwardRef<MailEditorRefProps, MailEditorProps>(
  (props, ref) => {
    //#region ------------------------------ Defaults
    const classes = useStyles();
    const {
      autofocus,
      body = '',
      toolbarContainer,
      textUpdated,
      messageIdentifier,
      projectId,
      isDraftIncluded = true,
      messageSubject,
      showAiAssistantButton = true,
    } = props;
    const { t } = useTranslation();
    //#endregion

    //#region ------------------------------ States / Attributes / Selectors
    const innerRef = useRef<FroalaEditor>(null);

    const isAIFeaturesEnabled = useSelector<RootReducerState, boolean>(
      (state) => getIsSettingEnabled(state, 'Cognitive_EnableAiFeatures')
    );

    const defaultEditorFont = useSelector<RootReducerState, Setting[]>(
      (state) => getSettingsByKey(state, 'General_DefaultFontSettings')
    );

    // Parse the stored jsonValue from settings into a usable object
    const parsedDefaultEditorFont = React.useMemo(() => {
      const raw = defaultEditorFont?.[0]?.jsonValue;
      const fallback = { defaultFont: 'Segoe UI', defaultFontSize: 14 };
      if (!raw) return fallback;

      try {
        const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
        return {
          defaultFont: parsed?.defaultFont ?? fallback.defaultFont,
          defaultFontSize:
            parsed && parsed.defaultFontSize
              ? Number(parsed.defaultFontSize)
              : fallback.defaultFontSize,
        };
      } catch (e) {
        console.warn('Could not parse General_DefaultFontSettings', e);
        return fallback;
      }
    }, [defaultEditorFont]);

    const bodyState = React.useMemo(
      () => (body.match(/^(<div><br><\/div>|\s*)$/) ? DEFAULT_BR_VALUE : body),
      [body]
    );

    const [showTextBlocksModal, setShowTextBlocksModal] =
      useState<boolean>(false);
    const [showInsertSignatureModal, setShowInsertSignatureModal] =
      useState<boolean>(false);
    const [editorFocused, setEditorFocused] = useState<boolean>(false);

    // Performance-optimiertes Debounced textUpdated callback
    const debouncedTextUpdated = React.useMemo(() => {
      if (!textUpdated) return undefined;

      let timeoutId: number;
      let lastValue: string = '';

      return (body: string) => {
        // Verhindere unnötige Updates wenn sich nichts geändert hat
        if (body === lastValue) return;
        lastValue = body;

        clearTimeout(timeoutId);
        timeoutId = window.setTimeout(() => {
          textUpdated(body);
        }, 100); // Etwas längeres Debouncing
      };
    }, [textUpdated]);

    // Refs für Throttling
    const configureAiPluginsLastCallRef = useRef<number>(0);
    const openAiAssistantLastCallRef = useRef<number>(0);
    //#endregion

    //#region ------------------------------ Methods / Handlers
    const handleCancelTextBlocks = useCallback(() => {
      setShowTextBlocksModal(false);
    }, []);

    const handleCancelInsertSignature = useCallback(() => {
      setShowInsertSignatureModal(false);
    }, []);

    const handleInsertTextBlocks = useCallback((textBlocks: TextBlock[]) => {
      try {
        if (innerRef?.current?.editorInstance) {
          innerRef.current.editorInstance.selection.restore();
          innerRef.current.editorInstance.html.insert(
            textBlocks.map((textBlock) => textBlock.content).join('\n')
          );
          innerRef.current.editorInstance.undo.saveStep();
        }
      } catch (error) {
        console.error('Error inserting text blocks:', error);
      }
      setShowTextBlocksModal(false);
    }, []);

    const handleInsertSignature = useCallback((value: string) => {
      try {
        if (innerRef?.current?.editorInstance) {
          innerRef.current.editorInstance.selection.restore();
          innerRef.current.editorInstance.html.insert(value);
          innerRef.current.editorInstance.undo.saveStep();
        }
      } catch (error) {
        console.error('Error inserting signature:', error);
      }
      setShowInsertSignatureModal(false);
    }, []);

    const handleClearFormatting = useCallback(() => {
      try {
        if (innerRef?.current?.editorInstance) {
          innerRef.current.editorInstance.commands.clearFormatting();
          innerRef.current.editorInstance.fontFamily.apply('sans-serif');
        }
      } catch (error) {
        console.error('Error clearing formatting:', error);
      }
    }, []);

    const handleInsertPlainFromClipboard = useCallback(() => {
      try {
        navigator.clipboard
          .readText()
          ?.then((text) => {
            if (innerRef?.current?.editorInstance) {
              innerRef.current.editorInstance.html.insert(text);
            }
          })
          .catch((error) => {
            console.error('Error reading from clipboard:', error);
          });
      } catch (error) {
        console.error('Error with clipboard operation:', error);
      }
    }, []);

    const handleDropEvent = useCallback((dropEvent) => {
      const itemList: DataTransferItemList =
        dropEvent?.originalEvent?.dataTransfer?.items ?? null;
      const files: FileList =
        dropEvent?.originalEvent?.dataTransfer?.files ?? null;

      var areNonImageFilesOrText: boolean = true;
      var isNoFile: boolean = false;

      if (itemList) {
        for (var i = 0; areNonImageFilesOrText && i < itemList.length; i++) {
          if (itemList[i]?.kind === 'file') {
            if (!itemList[i].type.match(/image\/(\D)+/)) {
              areNonImageFilesOrText = false;
            }
          } else {
            isNoFile = true;
          }
        }
      } else if (files) {
        for (
          var index = 0;
          areNonImageFilesOrText && index < files.length;
          index++
        ) {
          if (files.item(index).type.match(/image\/(\D)+/)) {
            areNonImageFilesOrText = false;
          }
        }
      } else {
        isNoFile = true;
      }

      if (areNonImageFilesOrText) {
        if (!isNoFile) {
          dropEvent.preventDefault();
          dropEvent.stopPropagation();
        }
        return true;
      }
      return false;
    }, []);

    // Configure AI plugins after editor initialization
    const configureAiPlugins = React.useCallback(() => {
      if (!innerRef?.current?.editorInstance) {
        return;
      }

      if (!isAIFeaturesEnabled) {
        return;
      }

      if (innerRef.current.editorInstance.aiAssistant) {
        const now = Date.now();
        if (now - configureAiPluginsLastCallRef.current < 100) {
          return;
        }
        configureAiPluginsLastCallRef.current = now;

        try {
          innerRef.current.editorInstance.aiAssistant.setConfig({
            messageIdentifier,
            messageSubject,
            isDraftIncluded,
            projectId,
            isAIFeaturesEnabled,
          });
        } catch (error) {
          console.error('Error configuring AI plugins:', error);
        }
      }
    }, [
      messageIdentifier,
      messageSubject,
      isDraftIncluded,
      projectId,
      isAIFeaturesEnabled,
    ]);

    // Method to open AI Assistant programmatically
    const openAiAssistant = React.useCallback(() => {
      if (
        innerRef?.current?.editorInstance?.aiAssistant &&
        isAIFeaturesEnabled
      ) {
        const now = Date.now();
        if (now - openAiAssistantLastCallRef.current < 500) {
          return;
        }
        openAiAssistantLastCallRef.current = now;

        innerRef.current.editorInstance.aiAssistant.openAiAssistant();
      }
    }, [isAIFeaturesEnabled]);

    // Handle editor focus states
    const handleEditorFocus = useCallback(() => {
      setEditorFocused(true);
    }, []);

    const handleEditorBlur = useCallback(() => {
      setEditorFocused(false);
    }, []);
    //#endregion

    //#region ------------------------------ Effects
    // Callback-Referenzen für die Registrierung
    const callbacksRef = useRef({
      setShowTextBlocksModal,
      setShowInsertSignatureModal,
      handleClearFormatting,
      handleInsertPlainFromClipboard,
    });

    // Update callbacks ref when functions change
    useEffect(() => {
      callbacksRef.current = {
        setShowTextBlocksModal,
        setShowInsertSignatureModal,
        handleClearFormatting,
        handleInsertPlainFromClipboard,
      };
    }, [
      setShowTextBlocksModal,
      setShowInsertSignatureModal,
      handleClearFormatting,
      handleInsertPlainFromClipboard,
    ]);

    // Icon/Command-Registrierung mit stabileren Referenzen
    useEffect(() => {
      // Reset the registration flag if we need to re-register with new callbacks
      iconsAndCommandsRegistered = false;

      registerIconsAndCommands(t, isAIFeaturesEnabled, callbacksRef.current);
    }, [t, isAIFeaturesEnabled]);

    const handleEditorContainerClick = useCallback((e: React.MouseEvent) => {
      if (e.target === e.currentTarget) {
        setTimeout(() => {
          if (innerRef?.current?.editorInstance) {
            try {
              innerRef.current.editorInstance.events.focus();
            } catch (error) {
              console.warn('Could not restore focus on click:', error);
            }
          }
        }, 10);
      }
    }, []);

    // Stabilisiere configureAiPlugins Referenz
    const configureAiPluginsStable = React.useRef(configureAiPlugins);
    configureAiPluginsStable.current = configureAiPlugins;

    // Configure AI plugins when component mounts or when related props change
    useEffect(() => {
      const timeoutId = setTimeout(() => {
        if (innerRef?.current?.editorInstance && isAIFeaturesEnabled) {
          configureAiPluginsStable.current();
        }
      }, 200);

      // Capture the current editor instance for cleanup
      const currentEditorInstance = innerRef?.current?.editorInstance;

      // Cleanup function für bessere Performance
      return () => {
        clearTimeout(timeoutId);
        if (currentEditorInstance?.aiAssistant?._destroy) {
          currentEditorInstance.aiAssistant._destroy();
        }
      };
    }, [
      messageIdentifier,
      messageSubject,
      isDraftIncluded,
      projectId,
      isAIFeaturesEnabled,
    ]);
    //#endregion

    // Performance-optimierte Froala-Konfiguration
    const config: FroalaConfig = React.useMemo(
      () => ({
        ...DEFAULT_FROALA_CONFIG,
        placeholderText: '',
        autofocus: autofocus,
        wordPasteModal: true,
        language: 'de',
        useClasses: false,
        toolbarBottom: true,
        toolbarContainer: `#${toolbarContainer}`,

        // Performance-Optimierungen
        typingTimer: 50,
        pasteAllowLocalImages: false,
        imageDefaultWidth: 0,

        //fonts
        fontFamilyDefaultSelection:
          parsedDefaultEditorFont?.defaultFont || 'Segoe UI',
        fontSizeDefaultSelection:
          parsedDefaultEditorFont?.defaultFontSize || 14,

        // Zusätzliche Performance-Einstellungen
        heightMin: null,
        heightMax: null,
        iframe: false,
        useHTML5: true,

        // Reduziere Event-Overhead
        charCounterCount: false,
        wordCounterCount: false,

        toolbarButtons: {
          moreText: {
            ...DEFAULT_TOOLBAR_BUTTONS.text,
            buttonsVisible: 5,
          },
          moreParagraph: {
            ...DEFAULT_TOOLBAR_BUTTONS.paragraph,
            buttonsVisible: 2,
          },
          moreRich: {
            buttons: [
              ...DEFAULT_TOOLBAR_BUTTONS.additional.buttons,
              // AI Assistant Button in toolbar
              ...(isAIFeaturesEnabled ? ['aiAssistant'] : []),
            ],
            buttonsVisible: 4,
          },
        },

        // AI plugins configuration
        ...(isAIFeaturesEnabled && {
          aiAssistant: {
            messageIdentifier: messageIdentifier,
            messageSubject: messageSubject,
            isDraftIncluded: isDraftIncluded,
            projectId: projectId,
            isAIFeaturesEnabled: isAIFeaturesEnabled,
          },
        }),

        quickInsertEnabled: true,
        quickInsertButtons: [
          'image',
          // 'video',
          'embedly',
          'table',
          'ul',
          'ol',
          'hr',
          'textBlocksQuick',
          'pastePlainText',
          'signatureInsert',
        ],

        events: {
          ...DEFAULT_FROALA_EVENTS,
          initialized: (editor: FroalaEditorInstance) => {
            // Safety check für editor
            if (!editor || !editor.events) {
              console.warn('Editor not properly initialized');
              return;
            }

            try {
              editor.events.on('drop', handleDropEvent, true);
              editor.events.on('focus', handleEditorFocus);
              editor.events.on('blur', handleEditorBlur);

              // Configure AI plugins after initialization
              if (isAIFeaturesEnabled) {
                setTimeout(() => {
                  configureAiPlugins();
                }, 100);
              }
            } catch (error) {
              console.error('Error setting up editor events:', error);
            }
          },
          'image.beforeUpload': function (files: any[]) {
            if (files.length) {
              if (files.length > 1) {
                for (let index = 0; index < files.length; index++) {
                  createFileInEditor(files[index], this);
                }
              } else {
                createFileInEditor(files[0], this);
              }
            }
            this.popups.hideAll();
            return false;
          },
          blur: function () {
            // Selection speichern beim Verlassen des Editors
            if (
              this &&
              this.selection &&
              typeof this.selection.save === 'function'
            ) {
              try {
                this.selection.save();
              } catch (e) {
                console.warn('Could not save selection:', e);
              }
            }
            handleEditorBlur();
          },
          focus: function () {
            handleEditorFocus();
            // Kurze Verzögerung, dann Selection wiederherstellen falls vorhanden
            setTimeout(() => {
              if (
                this &&
                this.selection &&
                typeof this.selection.restore === 'function'
              ) {
                try {
                  this.selection.restore();
                } catch (e) {
                  // Ignoriere Fehler beim Wiederherstellen
                }
              }
            }, 10);
          },
          contentChanged: function () {
            if (
              debouncedTextUpdated &&
              this &&
              this.html &&
              typeof this.html.get === 'function'
            ) {
              try {
                debouncedTextUpdated(this.html.get());
              } catch (error) {
                console.error('Error in contentChanged:', error);
              }
            }
          },
        },
      }),
      [
        autofocus,
        toolbarContainer,
        isAIFeaturesEnabled,
        messageIdentifier,
        messageSubject,
        isDraftIncluded,
        projectId,
        handleDropEvent,
        handleEditorFocus,
        handleEditorBlur,
        configureAiPlugins,
        debouncedTextUpdated,
        parsedDefaultEditorFont,
      ]
    );

    useImperativeHandle(ref, () => ({
      getHtml: () => {
        return innerRef?.current?.editorInstance?.html?.get() ?? '';
      },
      openAiAssistant: openAiAssistant,
    }));

    return (
      <div className={classes.root}>
        <div
          className={classes.editorContainer}
          onClick={handleEditorContainerClick}
        >
          <FroalaEditor
            ref={innerRef}
            config={config}
            model={bodyState}
            onModelChange={debouncedTextUpdated}
          />

          {/* Optional floating AI Assistant button */}
          {showAiAssistantButton && (
            <Button
              className={classes.aiAssistantButton}
              type="primary"
              size="small"
              onClick={openAiAssistant}
              tooltip={'Prio Buddy'}
              iconProp={['fal', 'message-bot']}
              style={{
                opacity: editorFocused ? 1 : 0.7,
                transition: 'opacity 0.2s ease',
              }}
            />
          )}
        </div>

        {showTextBlocksModal && (
          <TextBlocksModal
            visible={showTextBlocksModal}
            onCancelTextBlocks={handleCancelTextBlocks}
            insertTextBlocks={handleInsertTextBlocks}
            popoverVisible={true}
          />
        )}

        {showInsertSignatureModal && (
          <SignatureInsertModal
            visible={showInsertSignatureModal}
            onCancelSignatureInsert={handleCancelInsertSignature}
            insertSignatureInsert={handleInsertSignature}
          />
        )}
      </div>
    );
  }
);

export default MailEditor;
